import React, {
  MutableRefObject,
  useEffect,
  useState,
  useContext,
  FC,
} from 'react';
import styled from 'styled-components';

import { AriaAlertContext } from '../../AriaAlertContext';
import ArrowIconSVG from '../../assets/icons/chevron.svg';
import { colors } from '../constants';
import { getFieldClone, cloneField } from '../utilities/syntheticEventTracking';

import {
  ErrorMessageBubble,
  ErrorMessageBubbleIcon,
  ErrorMessageText,
} from './MessageBubble';
import { StyledInputProps, SVGProps } from './TextInput';

// DropDown

// Provides basic functionalities for select inputs, i.e. drop downs
// Only compatible with formik right now

// Interface --------------------------------------------------------------------------------------
interface DropDownProps {
  name: string;
  value: string;
  label?: string;
  options?: string[];
  disabled?: boolean;
  error?: string;
  touched?: boolean;
  showValidation?: boolean;
  slide?: boolean;
  resetLabel?: boolean;
  onFocus?: (() => void) | (() => undefined);
  onBlur?: (e: React.FocusEvent<any>) => void;
  onChange:
    | ((e: React.ChangeEvent<any>) => void)
    | (((e: React.ChangeEvent<any>) => void) & ((text: string) => void))
    | (() => undefined);
  required?: boolean;
  forwardRef?: MutableRefObject<any>;
}

// Styles -----------------------------------------------------------------------------------------
const Container = styled.div`
  position: relative;
  width: 100%;
`;
Container.displayName = 'Container';

const Select = styled.select<StyledInputProps>`
  height: 3.5rem;
  width: 100%;
  min-height: 3.5rem;
  border-radius: 0.125rem;
  border: 0.0625rem solid ${colors.BORDER_PRIMARY};
  padding: 0.75rem 2.1875rem 0 0.875rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  line-height: 2.75rem;
  font-size: 1rem;
  color: ${colors.TEXT_PRIMARY};
  text-align: left;
  outline: none;
  appearance: none;
  background-color: transparent;
  cursor: pointer;

  ${(p: StyledInputProps) =>
    p.showError &&
    `
    border-color: ${colors.TEXT_ERROR};
  `};

  /* Hover/Focus - change border color to accent color and adds box shadow */
  /* When an error exists, the border color and box shadow turn red */
  &:hover,
  &:focus {
    box-shadow: 0 0 0.25rem ${colors.UI_OUTLINE}26;
    border-color: ${colors.UI_OUTLINE};

    ${(p: StyledInputProps) =>
      p.showError &&
      `
      box-shadow: 0 0 0.25rem ${colors.TEXT_ERROR}26;
      border-color: ${colors.TEXT_ERROR};
    `};
  }

  /* undo on-focus UI if focus-visible property is supported */
  &:focus:not(:hover, :focus-visible) {
    box-shadow: none;
    border-color: ${colors.BORDER_PRIMARY};

    ${(p: StyledInputProps) =>
      p.showError &&
      `
      border-color: ${colors.TEXT_ERROR};
    `};
  }

  /* apply on-focus UI only when keyboard-focused */
  &:focus-visible {
    box-shadow: 0 0 0.25rem ${colors.UI_OUTLINE}26;
    border-color: ${colors.UI_OUTLINE};

    ${(p: StyledInputProps) =>
      p.showError &&
      `
      box-shadow: 0 0 0.25rem ${colors.TEXT_ERROR}26;
      border-color: ${colors.TEXT_ERROR};
    `};
  }

  /* Disabled - remove hovering border color change and box shadow */
  &:disabled {
    box-shadow: none;
    border-color: ${colors.BORDER_PRIMARY};
    background-color: #efefef4d;
    opacity: 1;
    cursor: no-drop;
  }
`;
Select.displayName = 'Select';

const Label = styled.label<StyledInputProps>`
  position: absolute;
  top: 0;
  left: 1rem;
  display: flex;
  justify-content: center;
  line-height: 3.5rem;
  font-size: 1rem;
  color: ${colors.TEXT_LABEL};
  pointer-events: none;
  transition: transform 0.175s ease-out 0s;
  transform-origin: left top;

  ${(p: StyledInputProps) =>
    p.slide &&
    `
    transform: translateY(-12%) scale(0.75);
  `};
`;
Label.displayName = 'Label';

const Arrow = styled(ArrowIconSVG)<SVGProps>`
  position: absolute;
  top: 1.5rem;
  right: 1rem;
  pointer-events: none;

  ${(p: SVGProps) =>
    p.isOpen &&
    `
    transform: rotate(180deg);
  `};
`;
Arrow.displayName = 'Arrow';

// Hidden error message is revealed when an error exists for a touched input
// Props:
// isVisible - toggles display between flex and none
const ErrorMessage = styled(ErrorMessageBubble)<StyledInputProps>`
  display: ${(p: StyledInputProps) => (p.isVisible ? 'flex' : 'none')};
`;
ErrorMessage.displayName = 'ErrorMessage';

// Component --------------------------------------------------------------------------------------
export const DropDown: FC<DropDownProps> = ({
  name,
  value,
  label,
  options,
  disabled,
  error,
  touched,
  showValidation,
  slide,
  resetLabel,
  onFocus,
  onBlur,
  onChange,
  required,
  forwardRef,
}) => {
  // State ----------------------------------------------------------------------------------------
  // Transform label when input is focused or has a nonempty value
  const [adjustLabel, setAdjustLabel] = useState(false);

  // Show inset validation icon and any error message after input has been touched
  const [showValidationError, setShowValidationError] = useState(
    touched && showValidation
  );

  // Effects --------------------------------------------------------------------------------------
  const { setAriaAlertMessage } = useContext(AriaAlertContext);

  // Functions ------------------------------------------------------------------------------------
  // onFocus - controls label animation and calls focus handler from props
  const handleFocus = () => {
    // Move aside the label when input is focused
    if (slide === true) {
      setAdjustLabel(true);
    }
    if (onFocus) onFocus();
  };

  // onBlur - changes state to indicate input is touched and calls blur handler from props
  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    // Show validation now that input has been touched
    setShowValidationError(touched && showValidation);

    // Reset label to initial position/scale if input value is still empty
    if (value === undefined || (value !== undefined && value.length === 0)) {
      setAdjustLabel(false);
    }
    if (error && setAriaAlertMessage) {
      setAriaAlertMessage(error);
    }
    // Trigger synthetic change for Heap tracking purposes
    trackChangeOnBlur(e);
    // Call blur handler from props with the entire event object
    if (onBlur) onBlur(e);
  };

  // Catch-all workaround to inconsistent browser event tracking
  function trackChangeOnBlur(e: React.FocusEvent<HTMLSelectElement>) {
    // Clone the blur event and dispatch it as a change on a corresponding input element clone
    try {
      // Cast the blur event clone as a change event
      const syntheticEvent = new Event('change', e.nativeEvent);
      // Get the ID and form of the blurred input field
      const { id, form } = e.target;
      // Get the corresponding element clone if one exists, otherwise create one
      let clone = getFieldClone(id);
      const firstBlurWithChange = !clone && value !== '';
      if (!clone) clone = cloneField(id, document.getElementById(id), form);
      // Dispatch the synthetic event to simulate the change when the clone/real values differ
      const cloneValue = clone.getAttribute('value');
      if (cloneValue !== value || firstBlurWithChange) {
        clone.dispatchEvent(syntheticEvent);
        clone.setAttribute('value', value);
      }
    } catch {
      // TO-DO error handler - probably not worth a NPC-Client-Error
    }
  }

  // Show validation for touched inputs
  useEffect(() => {
    setShowValidationError(touched && showValidation);
  }, [touched]);

  // Slide up and shrink the input label if input value is nonempty
  useEffect(() => {
    if (value !== undefined && value.length > 0) {
      setAdjustLabel(slide);
    }
  }, [value]);

  // Reset sliding label when autocompleted value is cleared with an X button
  useEffect(() => {
    if (resetLabel && adjustLabel && !value) {
      setAdjustLabel(false);
      if (forwardRef && forwardRef.current) {
        forwardRef.current.focus();
        forwardRef.current.blur();
      }
    }
  }, [resetLabel]);

  // Render ---------------------------------------------------------------------------------------
  return (
    <Container>
      <Select
        id={name}
        name={name}
        value={value}
        disabled={disabled}
        showError={!!(error && showValidationError)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={onChange}
        aria-required={required}
        aria-describedby={`${name}-error`}
        aria-invalid={!!(error && showValidationError)}
        ref={forwardRef}
      >
        {options &&
          options.map((state: string, index: number) => (
            <option id={`${name}-option-${index}`} key={state}>
              {state}
            </option>
          ))}
      </Select>
      <Label className="dropdown-label" htmlFor={name} slide={adjustLabel}>
        {label}
      </Label>
      <Arrow />
      <ErrorMessage isVisible={!!(error && showValidationError)}>
        <ErrorMessageText id={`${name}-error`}>{error}</ErrorMessageText>
        <ErrorMessageBubbleIcon />
      </ErrorMessage>
    </Container>
  );
};

DropDown.defaultProps = {
  label: '',
  options: [],
  disabled: false,
  error: undefined,
  touched: undefined,
  showValidation: true,
  slide: true,
  resetLabel: false,
  onFocus: () => {},
  onBlur: () => {},
  required: true,
  forwardRef: undefined,
};
